/*
 * TracePanel — terminal-style streaming reasoning panel.
 * Ported from ui-handoff/.../trace.jsx. Replaces the prototype's TRACE_PROGRAM
 * mock with live SSE events from the backend.
 *
 * Event renderers are keyed by `kind` (plan, tool_call, verify, done, error,
 * stream_end). Each block matches the prototype's exact styling.
 */

import React from 'react';

function fmtTime(ms) {
  const total = (ms || 0) / 1000;
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toFixed(2).padStart(5, '0');
  return `${m}:${s}`;
}

function PlanBlock({ event }) {
  return (
    <div className="tr-plan">
      <div className="tr-tool-hd">
        <span className="tr-arrow" style={{ color: 'var(--terminal-pos)' }}>◆</span>
        <span className="tr-tool-name">plan</span>
        <span style={{ color: 'var(--terminal-pos)' }}>{event.title}</span>
      </div>
      {(event.body || []).map((line, i) => (
        <div key={i} className="tr-line">
          <span className="tr-gut">│</span> {line}
        </div>
      ))}
    </div>
  );
}

function ToolCallBlock({ event, isLatest }) {
  const output = event.output || [];
  const hasOutput = output.length > 0;
  return (
    <div className="tr-tool">
      <div className="tr-tool-hd">
        <span className="tr-arrow">▸</span>
        <span className="tr-tool-name">tool_call</span>
        <span className="tr-tool-fn">{event.name}</span>
        <span className="tr-tool-args">({event.args})</span>
        {!hasOutput && <span className="tr-spinner" aria-hidden>◐</span>}
      </div>
      {hasOutput && (
        <div className="tr-tool-out">
          {output.map((line, i) => (
            <div key={i} className="tr-line">
              <span className="tr-gut">│</span> {line}
            </div>
          ))}
        </div>
      )}
      {!hasOutput && isLatest && (
        <div className="tr-line">
          <span className="tr-gut">│</span> <span className="tr-cursor">▌</span>
        </div>
      )}
    </div>
  );
}

function VerifyBlock({ event }) {
  return (
    <div className="tr-verify">
      <div className="tr-tool-hd">
        <span className="tr-arrow" style={{ color: 'var(--terminal-pos)' }}>✓</span>
        <span className="tr-tool-name">verify</span>
        <span style={{ color: 'var(--terminal-accent)' }}>{event.clip_id}</span>
        <span className="tr-tool-args">[{event.span}]</span>
      </div>
      {(event.checks || []).map((check, i) => {
        const failed = check.ok === false;
        const warned = check.ok === 'warn';
        const color = failed
          ? 'var(--terminal-err)'
          : warned
            ? 'var(--terminal-warn)'
            : 'var(--terminal-pos)';
        const symbol = failed ? '✗' : warned ? '!' : '✓';
        return (
        <div key={i} className="tr-line">
          <span className="tr-gut">│</span>
          <span style={{ color, display: 'inline-block', width: 14 }}>{symbol}</span>
          <span style={{ color: 'var(--terminal-dim)', width: 120, display: 'inline-block' }}>
            {check.label}
          </span>
          <span>{check.detail}</span>
        </div>
        );
      })}
      <div className="tr-line" style={{ marginTop: 2 }}>
        <span className="tr-gut">│</span>
        <span style={{ color: 'var(--terminal-dim)', width: 14, display: 'inline-block' }} />
        <span style={{ color: 'var(--terminal-dim)', width: 120, display: 'inline-block' }}>
          confidence
        </span>
        <span
          style={{
            color: event.score > 0.85 ? 'var(--terminal-pos)' : 'var(--terminal-warn)',
          }}
        >
          {(event.score || 0).toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function DoneBlock({ event }) {
  return (
    <div className="tr-tool-hd" style={{ marginTop: 10 }}>
      <span style={{ color: 'var(--terminal-pos)' }}>●</span>
      <span style={{ color: 'var(--terminal-pos)' }}>done</span>
      <span style={{ color: 'var(--terminal-ink)' }}>{event.summary}</span>
    </div>
  );
}

function ErrorBlock({ event }) {
  return (
    <div className="tr-tool" style={{ marginTop: 10 }}>
      <div className="tr-tool-hd">
        <span style={{ color: 'var(--terminal-err)' }}>✗</span>
        <span style={{ color: 'var(--terminal-err)' }}>error</span>
        {event.tool && (
          <span style={{ color: 'var(--terminal-accent)' }}>{event.tool}</span>
        )}
        {event.status_code != null && (
          <span className="tr-tool-args">[{event.status_code}]</span>
        )}
      </div>
      <div className="tr-line">
        <span className="tr-gut">│</span>
        <span style={{ color: 'var(--terminal-err)' }}>{event.message}</span>
      </div>
    </div>
  );
}

function renderEvent(event, isLatest) {
  switch (event.kind) {
    case 'plan':
      return <PlanBlock event={event} />;
    case 'tool_call':
      return <ToolCallBlock event={event} isLatest={isLatest} />;
    case 'verify':
      return <VerifyBlock event={event} />;
    case 'done':
      return <DoneBlock event={event} />;
    case 'error':
      return <ErrorBlock event={event} />;
    default:
      return null;
  }
}

export function TracePanel({
  events,
  status,
  query,
  elapsedMs,
  collapsed,
  onToggle,
}) {
  // Suppress the sentinel stream_end event in the body; it drives status only.
  const renderable = events.filter((e) => e.kind !== 'stream_end');
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollRef.current && !collapsed) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [renderable.length, collapsed]);

  const isStreaming = status === 'loading';

  return (
    <aside className={`trace ${collapsed ? 'trace-collapsed' : ''}`}>
      <header className="trace-hd">
        <div className="trace-hd-l">
          <span className="trace-dot" />
          {!collapsed && (
            <>
              <span className="mono micro" style={{ color: 'var(--terminal-ink)', opacity: 0.85 }}>
                AGENT TRACE
              </span>
              <span className="mono trace-status" style={traceStatusStyle(status)}>
                {traceStatusLabel(status)}
              </span>
            </>
          )}
        </div>
        <div className="trace-hd-r">
          {!collapsed && (
            <span className="mono micro" style={{ color: 'var(--terminal-dim)' }}>
              {fmtTime(elapsedMs)}
            </span>
          )}
          <button
            type="button"
            className="trace-toggle mono"
            onClick={onToggle}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '◀' : '▶'}
          </button>
        </div>
      </header>

      {!collapsed && (
        <div className="trace-body" ref={scrollRef}>
          <div className="tr-line" style={{ color: 'var(--terminal-dim)', marginBottom: 8 }}>
            <span style={{ color: 'var(--terminal-accent)' }}>$</span>{' '}
            agent.run <span style={{ color: 'var(--terminal-ink)' }}>--query</span> "{query}"
          </div>
          {renderable.length === 0 && isStreaming && (
            <div className="tr-line" style={{ color: 'var(--terminal-dim)' }}>
              connecting…
            </div>
          )}
          {renderable.map((event, i) => (
            <React.Fragment key={`${event.kind}-${i}-${event.t_ms || 0}`}>
              {renderEvent(event, i === renderable.length - 1 && isStreaming)}
            </React.Fragment>
          ))}
          {isStreaming && (
            <div className="tr-line" style={{ color: 'var(--terminal-dim)', marginTop: 8 }}>
              <span className="tr-cursor">▌</span>
            </div>
          )}
        </div>
      )}

      {collapsed && (
        <div className="trace-collapsed-hint mono">
          <div>{traceStatusLabel(status)}</div>
          <div style={{ color: 'var(--terminal-dim)', marginTop: 6 }}>click ◀ to expand</div>
        </div>
      )}

      <style>{`
        .trace {
          background: var(--terminal-bg); color: var(--terminal-ink);
          font-family: var(--mono); font-size: 12px; line-height: 1.55;
          height: 100%; display: flex; flex-direction: column;
          border-left: 1px solid var(--rule-2);
        }
        .trace-hd {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px; border-bottom: 1px solid rgba(233,226,207,0.08);
          flex-shrink: 0;
        }
        .trace-hd-l { display: flex; align-items: center; gap: 10px; }
        .trace-hd-r { display: flex; align-items: center; gap: 10px; }
        .trace-dot { width: 8px; height: 8px; border-radius: 99px; background: var(--terminal-accent); box-shadow: 0 0 8px var(--terminal-accent); animation: pulse 1.4s ease-in-out infinite; }
        @keyframes pulse { 50% { opacity: 0.4; } }
        .trace-status { font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--terminal-accent); }
        .trace-toggle {
          background: transparent; border: 1px solid rgba(233,226,207,0.18);
          color: var(--terminal-ink); width: 24px; height: 22px; font-size: 10px; padding: 0;
          display: grid; place-items: center;
        }
        .trace-toggle:hover { background: rgba(233,226,207,0.06); }
        .trace-body { flex: 1; overflow-y: auto; padding: 14px 16px 24px; }
        .trace-body::-webkit-scrollbar { width: 8px; }
        .trace-body::-webkit-scrollbar-thumb { background: rgba(233,226,207,0.18); border-radius: 4px; }
        .tr-line { white-space: pre-wrap; }
        .tr-plan { margin-bottom: 10px; }
        .tr-tool { margin: 8px 0; }
        .tr-verify { margin: 8px 0; }
        .tr-tool-hd { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
        .tr-arrow { color: var(--terminal-accent); }
        .tr-tool-name { color: var(--terminal-dim); }
        .tr-tool-fn { color: var(--terminal-accent); font-weight: 500; }
        .tr-tool-args { color: var(--terminal-ink); opacity: .9; }
        .tr-tool-out { margin-top: 4px; }
        .tr-gut { color: var(--terminal-dim); margin-right: 4px; }
        .tr-spinner { color: var(--terminal-accent); animation: spin 1s linear infinite; display: inline-block; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .tr-cursor { background: var(--terminal-accent); color: var(--terminal-accent); width: 7px; display: inline-block; height: 1em; vertical-align: -2px; animation: blink 1s steps(2) infinite; }
        @keyframes blink { 50% { opacity: 0; } }
        .trace-collapsed { width: 44px; }
        .trace-collapsed-hint { padding: 14px 8px; color: var(--terminal-ink); writing-mode: vertical-rl; transform: rotate(180deg); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; }
      `}</style>
    </aside>
  );
}

function traceStatusLabel(status) {
  switch (status) {
    case 'loading':
      return 'streaming';
    case 'results':
      return 'complete';
    case 'empty':
      return 'no match';
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
}

function traceStatusStyle(status) {
  if (status === 'results') return { color: 'var(--terminal-pos)' };
  if (status === 'error') return { color: 'var(--terminal-err)' };
  if (status === 'empty') return { color: 'var(--terminal-warn)' };
  return undefined;
}
